/**
 * Dealer Showroom Health Points Score (DSHPS) Scoring Engine.
 *
 * 100 points across 8 pathways, tailored for automotive dealership environments.
 * 6 mandatory preconditions + ~45 features earning 1-3 points each.
 *
 * Reweighted for retail/showroom: higher wayfinding (customer journey),
 * higher visual/air (merchandise presentation + indoor air from service bays).
 *
 * Features tagged with Ford Signature 2.0 pillar alignment.
 */

// Pathway caps (100 total, reweighted for dealer showroom)
export const DS_PATHWAY_CAPS = {
  daylight: 15,
  noise: 12,
  enrichment: 15,
  nature: 10,
  movement: 10,
  visual: 12,
  air_quality: 12,
  wayfinding: 14,
};

// Preconditions (mandatory, 0 pts) — same safety thresholds
export const DS_PRECONDITIONS = [
  { id: 'P1', pathway: 'daylight', label: 'Minimum daylight >=150 lux at customer areas', threshold: '>=150 lux' },
  { id: 'P2', pathway: 'noise', label: 'Noise safety <80 dB sustained', threshold: '<80 dB' },
  { id: 'P3', pathway: 'air_quality', label: 'Basic air quality PM2.5 <35 ug/m3', threshold: '<35 ug/m3' },
  { id: 'P4', pathway: 'visual', label: 'Visual safety pattern stress <0.75', threshold: '<0.75' },
  { id: 'P5', pathway: 'wayfinding', label: 'Emergency wayfinding: exit signs visible', threshold: 'manual' },
  { id: 'P6', pathway: 'movement', label: 'Accessible circulation: ADA-compliant paths', threshold: 'manual' },
];

// Feature catalog (~45 features, with Signature 2.0 pillar tags)
export const DS_FEATURES = [
  // Daylight (15 pts)
  { id: 'D1', pathway: 'daylight', points: 3, label: 'Vehicle display lighting >=500 lux', pillar: 'discover_ford', autoCheck: s => s.daylight_lux >= 500 },
  { id: 'D2', pathway: 'daylight', points: 3, label: 'Customer areas >=400 lux at task height', pillar: 'hospitality_first', autoCheck: s => s.daylight_lux >= 400 },
  { id: 'D3', pathway: 'daylight', points: 2, label: 'Tunable color temperature lighting (2700-5000K)', pillar: 'discover_ford', autoCheck: null },
  { id: 'D4', pathway: 'daylight', points: 2, label: 'Skylights or clerestory windows in showroom', pillar: 'discover_ford', autoCheck: null },
  { id: 'D5', pathway: 'daylight', points: 2, label: 'Daylight-responsive dimming system', pillar: 'operations_excellence', autoCheck: null },
  { id: 'D6', pathway: 'daylight', points: 2, label: 'Dedicated task lighting at sales desks >=500 lux', pillar: 'sales_service', autoCheck: null },
  { id: 'D7', pathway: 'daylight', points: 1, label: 'Glazed showroom facade with daylight penetration', pillar: 'discover_ford', autoCheck: null },
  // Noise (12 pts)
  { id: 'N1', pathway: 'noise', points: 3, label: 'Customer areas <=55 dB for conversation comfort', pillar: 'hospitality_first', autoCheck: s => s.noise_db <= 55 },
  { id: 'N2', pathway: 'noise', points: 2, label: 'Service waiting area <=60 dB', pillar: 'hospitality_first', autoCheck: s => s.noise_db <= 60 },
  { id: 'N3', pathway: 'noise', points: 2, label: 'Acoustic ceiling (NRC >=0.75, >=60% coverage)', pillar: 'hospitality_first', autoCheck: null },
  { id: 'N4', pathway: 'noise', points: 2, label: 'Sound isolation between service bay and showroom', pillar: 'operations_excellence', autoCheck: null },
  { id: 'N5', pathway: 'noise', points: 2, label: 'Acoustic treatment in customer lounge', pillar: 'hospitality_first', autoCheck: null },
  { id: 'N6', pathway: 'noise', points: 1, label: 'Sound masking system in sales offices', pillar: 'sales_service', autoCheck: null },
  // Nature/Biophilia (10 pts)
  { id: 'B1', pathway: 'nature', points: 2, label: 'Indoor plants >=1 per 100 sq ft customer area', pillar: 'hospitality_first', autoCheck: null },
  { id: 'B2', pathway: 'nature', points: 2, label: 'Living wall or green feature in showroom', pillar: 'discover_ford', autoCheck: null },
  { id: 'B3', pathway: 'nature', points: 2, label: 'Nature views from >=50% of customer seating', pillar: 'hospitality_first', autoCheck: s => s.nature_view },
  { id: 'B4', pathway: 'nature', points: 2, label: 'Natural materials (wood, stone) in primary finishes', pillar: 'hospitality_first', autoCheck: null },
  { id: 'B5', pathway: 'nature', points: 1, label: 'Water feature in showroom or lounge', pillar: 'hospitality_first', autoCheck: null },
  { id: 'B6', pathway: 'nature', points: 1, label: 'Outdoor waiting area with landscaping', pillar: 'hospitality_first', autoCheck: null },
  // Enrichment (15 pts) — includes new Signature 2.0 features E7, E8
  { id: 'E1', pathway: 'enrichment', points: 2, label: 'Visual interest: brand storytelling, focal displays', pillar: 'discover_ford', autoCheck: null },
  { id: 'E2', pathway: 'enrichment', points: 3, label: 'Spatial diversity: >=2 distinct customer zones', pillar: 'hospitality_first', autoCheck: null },
  { id: 'E3', pathway: 'enrichment', points: 2, label: 'Sensory variety: varied textures, materials, lighting', pillar: 'discover_ford', autoCheck: null },
  { id: 'E4', pathway: 'enrichment', points: 3, label: 'Social affordances: private + group seating options', pillar: 'hospitality_first', autoCheck: null },
  { id: 'E5', pathway: 'enrichment', points: 2, label: 'Interactive displays or vehicle configuration stations', pillar: 'discover_ford', autoCheck: null },
  { id: 'E6', pathway: 'enrichment', points: 3, label: 'Cognitive engagement: digital media, product info, kids area', pillar: 'discover_ford', autoCheck: null },
  { id: 'E7', pathway: 'enrichment', points: 3, label: 'Hub/Welcome hospitality station with food & beverage', pillar: 'hospitality_first', autoCheck: null },
  { id: 'E8', pathway: 'enrichment', points: 2, label: 'Flexible consultation spaces (desk + lounge options)', pillar: 'sales_service', autoCheck: null },
  // Movement (10 pts)
  { id: 'M1', pathway: 'movement', points: 2, label: 'Customer walking route >=50m through showroom', pillar: 'discover_ford', autoCheck: s => s.movement_minutes_day > 0 },
  { id: 'M2', pathway: 'movement', points: 2, label: 'Vehicle display layout encourages browsing circulation', pillar: 'discover_ford', autoCheck: null },
  { id: 'M3', pathway: 'movement', points: 2, label: 'Standing-height consultation surfaces', pillar: 'sales_service', autoCheck: null },
  { id: 'M4', pathway: 'movement', points: 2, label: 'Outdoor test-drive staging accessible on foot', pillar: 'sales_service', autoCheck: null },
  { id: 'M5', pathway: 'movement', points: 2, label: 'Staff break area with movement opportunity', pillar: 'operations_excellence', autoCheck: null },
  // Air Quality (12 pts)
  { id: 'A1', pathway: 'air_quality', points: 2, label: 'PM2.5 <15 ug/m3 in customer areas', pillar: 'hospitality_first', autoCheck: s => s.pm25_ug_m3 < 15 },
  { id: 'A2', pathway: 'air_quality', points: 2, label: 'PM2.5 <8 ug/m3 in customer areas', pillar: 'hospitality_first', autoCheck: s => s.pm25_ug_m3 < 8 },
  { id: 'A3', pathway: 'air_quality', points: 2, label: 'MERV 13+ HVAC filtration', pillar: 'operations_excellence', autoCheck: null },
  { id: 'A4', pathway: 'air_quality', points: 2, label: 'Positive pressure in showroom vs service bay', pillar: 'operations_excellence', autoCheck: null },
  { id: 'A5', pathway: 'air_quality', points: 2, label: 'Vehicle exhaust extraction in delivery bay', pillar: 'operations_excellence', autoCheck: null },
  { id: 'A6', pathway: 'air_quality', points: 1, label: 'Visible air quality monitor', pillar: 'hospitality_first', autoCheck: null },
  { id: 'A7', pathway: 'air_quality', points: 1, label: 'Low-VOC finishes and adhesives', pillar: 'operations_excellence', autoCheck: null },
  // Wayfinding (14 pts) — includes new Signature 2.0 feature W8
  { id: 'W1', pathway: 'wayfinding', points: 3, label: 'Clear customer journey signage at all decision points', pillar: 'sales_service', autoCheck: null },
  { id: 'W2', pathway: 'wayfinding', points: 2, label: 'Distinctive landmarks at key junctions', pillar: 'sales_service', autoCheck: null },
  { id: 'W3', pathway: 'wayfinding', points: 2, label: 'Visual sightlines from entry to key departments', pillar: 'sales_service', autoCheck: s => s.wayfinding_score >= 0.7 },
  { id: 'W4', pathway: 'wayfinding', points: 2, label: 'Intuitive department layout (sales → F&I → delivery)', pillar: 'sales_service', autoCheck: null },
  { id: 'W5', pathway: 'wayfinding', points: 2, label: 'Color or material coding by department', pillar: 'discover_ford', autoCheck: null },
  { id: 'W6', pathway: 'wayfinding', points: 2, label: 'Digital directory or you-are-here maps', pillar: 'sales_service', autoCheck: null },
  { id: 'W7', pathway: 'wayfinding', points: 1, label: 'Service lane clearly marked from exterior', pillar: 'operations_excellence', autoCheck: null },
  { id: 'W8', pathway: 'wayfinding', points: 2, label: 'Intuitive sales→F&I→delivery customer journey flow', pillar: 'sales_service', autoCheck: null },
  // Visual Comfort (12 pts)
  { id: 'V1', pathway: 'visual', points: 3, label: 'No uniform grid patterns on major surfaces', pillar: 'hospitality_first', autoCheck: s => s.visual_pattern_stress < 0.3 },
  { id: 'V2', pathway: 'visual', points: 2, label: 'Natural/organic patterns on finishes', pillar: 'hospitality_first', autoCheck: null },
  { id: 'V3', pathway: 'visual', points: 2, label: 'Varied geometry, curved design elements', pillar: 'discover_ford', autoCheck: null },
  { id: 'V4', pathway: 'visual', points: 2, label: 'Anti-glare surfaces on showroom floor', pillar: 'discover_ford', autoCheck: null },
  { id: 'V5', pathway: 'visual', points: 2, label: 'Non-uniform, layered lighting design', pillar: 'discover_ford', autoCheck: null },
  { id: 'V6', pathway: 'visual', points: 1, label: 'Matte or satin vehicle display backdrop', pillar: 'discover_ford', autoCheck: null },
];

export const DS_FEATURE_MAP = Object.fromEntries(DS_FEATURES.map(f => [f.id, f]));

/**
 * Check all 6 preconditions against a space.
 */
export function checkDsPreconditions(space) {
  return {
    P1: (space.daylight_lux || 0) >= 150,
    P2: (space.noise_db || 999) < 80,
    P3: (space.pm25_ug_m3 || 999) < 35,
    P4: (space.visual_pattern_stress || 0) < 0.75,
    P5: true, // Manual
    P6: true, // Manual
  };
}

/**
 * Auto-detect features from space measurements.
 */
export function autoDetectDsFeatures(space) {
  const detected = {};
  for (const f of DS_FEATURES) {
    if (f.autoCheck) {
      try {
        detected[f.id] = f.autoCheck(space);
      } catch {
        detected[f.id] = false;
      }
    }
  }
  return detected;
}

/**
 * Score a single space.
 */
export function scoreDsSpace(space, featureStatuses = {}) {
  const preconditions = checkDsPreconditions(space);
  const allPass = Object.values(preconditions).every(v => v);
  const auto = autoDetectDsFeatures(space);

  const pathwayPoints = {};
  for (const pw of Object.keys(DS_PATHWAY_CAPS)) {
    pathwayPoints[pw] = 0;
  }

  const features = DS_FEATURES.map(f => {
    const manualStatus = featureStatuses[f.id] || 'not_pursuing';
    const autoResult = auto[f.id];

    let status, autoDetected;
    if (manualStatus === 'earned') {
      status = 'earned';
      autoDetected = false;
    } else if (autoResult === true) {
      status = 'earned';
      autoDetected = true;
    } else {
      status = manualStatus;
      autoDetected = false;
    }

    let earnedPts = status === 'earned' ? f.points : 0;
    if (pathwayPoints[f.pathway] + earnedPts > DS_PATHWAY_CAPS[f.pathway]) {
      earnedPts = Math.max(0, DS_PATHWAY_CAPS[f.pathway] - pathwayPoints[f.pathway]);
    }
    pathwayPoints[f.pathway] += earnedPts;

    return {
      featureId: f.id,
      status,
      points: earnedPts,
      label: f.label,
      pathway: f.pathway,
      pillar: f.pillar,
      autoDetected,
    };
  });

  const totalPoints = Object.values(pathwayPoints).reduce((a, b) => a + b, 0);
  const tier = allPass ? getDsCertificationTier(totalPoints) : 'none';

  return {
    spaceId: space.id,
    spaceName: space.name,
    preconditionsPassed: allPass,
    preconditionResults: preconditions,
    totalPoints,
    pathwayPoints,
    pathwayCaps: DS_PATHWAY_CAPS,
    tier,
    features,
  };
}

/**
 * Score an entire project (WELL/LEED-style tiered-threshold scoring).
 */
export function scoreDsProject(spacesWithFeatures) {
  const numSpaces = spacesWithFeatures.length;
  if (numSpaces === 0) {
    return {
      totalPoints: 0, pathwayPoints: {}, pathwayCaps: DS_PATHWAY_CAPS,
      tier: 'none', allPreconditionsPass: false, projectFeatures: [],
      spaceScores: [], avgScore: 0,
    };
  }

  const spaceScores = spacesWithFeatures.map(({ space, featureStatuses }) =>
    scoreDsSpace(space, featureStatuses)
  );

  const allPreconditionsPass = spaceScores.every(ss => ss.preconditionsPassed);

  const featureEarnedCounts = {};
  for (const f of DS_FEATURES) {
    featureEarnedCounts[f.id] = spaceScores.filter(ss => {
      const sf = ss.features.find(feat => feat.featureId === f.id);
      return sf && sf.status === 'earned';
    }).length;
  }

  const pathwayPoints = {};
  for (const pw of Object.keys(DS_PATHWAY_CAPS)) {
    pathwayPoints[pw] = 0;
  }

  const projectFeatures = DS_FEATURES.map(f => {
    const earnedCount = featureEarnedCounts[f.id] || 0;
    const pctEarned = earnedCount / numSpaces;
    const isAutoDetectable = f.autoCheck !== null;

    let earnedPts = 0;
    let complianceLevel = 'none';

    if (isAutoDetectable) {
      if (pctEarned >= 0.75) {
        earnedPts = f.points;
        complianceLevel = 'full';
      } else if (pctEarned >= 0.50) {
        earnedPts = Math.ceil(f.points / 2);
        complianceLevel = 'partial';
      }
    } else {
      if (earnedCount > 0) {
        earnedPts = f.points;
        complianceLevel = 'full';
      }
    }

    if (pathwayPoints[f.pathway] + earnedPts > DS_PATHWAY_CAPS[f.pathway]) {
      earnedPts = Math.max(0, DS_PATHWAY_CAPS[f.pathway] - pathwayPoints[f.pathway]);
    }
    pathwayPoints[f.pathway] += earnedPts;

    return {
      featureId: f.id,
      label: f.label,
      pathway: f.pathway,
      pillar: f.pillar,
      maxPoints: f.points,
      earnedPts,
      earnedCount,
      totalSpaces: numSpaces,
      pctCompliant: Math.round(pctEarned * 100),
      complianceLevel,
      isAutoDetectable,
    };
  });

  const totalPoints = Object.values(pathwayPoints).reduce((a, b) => a + b, 0);
  const tier = allPreconditionsPass ? getDsCertificationTier(totalPoints) : 'none';

  return {
    totalPoints,
    pathwayPoints,
    pathwayCaps: DS_PATHWAY_CAPS,
    tier,
    allPreconditionsPass,
    projectFeatures,
    spaceScores,
    avgScore: totalPoints,
  };
}

/**
 * Get certification tier from score.
 */
export function getDsCertificationTier(score) {
  score = Math.round(score);
  if (score >= 75) return 'platinum';
  if (score >= 60) return 'gold';
  if (score >= 45) return 'silver';
  if (score >= 30) return 'bronze';
  return 'none';
}
